import { Truck } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { DateInput } from '@/components/date-input';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';
import {
  EWB_CANCEL_REASONS,
  EWB_MODES,
  EWB_SUB_SUPPLY,
  EWB_VEHICLE_REASONS,
  GST_STATES,
  TEST_CONNECTION_HINT,
  useEwayBill,
  useEwayBillAction,
  type EwbTransport,
} from '@/lib/gst';
import { isIndia, orgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

const errorText = (e: unknown) =>
  e instanceof ApiError
    ? Array.isArray(e.details) && e.details.length
      ? (e.details as Array<{ message: string }>).map((d) => d.message)
      : [e.message]
    : [String(e)];

/**
 * E-way bill strip on an approved India invoice or a delivery challan (document 06 T10): whether
 * one is needed (₹50,000, or inter-state job work), the bill once generated with its validity and
 * vehicle, and Generate, Update vehicle (Part B) and Cancel (within 24 hours). Nothing for UAE
 * clients. While a bill is active the document cannot be edited, voided or cancelled.
 */
export function EwayBillPanel({
  documentId,
  number,
}: {
  documentId: string;
  number: string | null;
}) {
  const india = isIndia();
  const q = useEwayBill(documentId, india);
  const me = useMe();
  const [dialog, setDialog] = useState<'generate' | 'vehicle' | 'cancel' | null>(null);
  if (!india || !q.data) return null;
  const { required, ewayBill: bill, irn, defaults, goods } = q.data;
  const staff = me.data?.isStaff ?? false;
  const active = bill?.status === 'active' ? bill : null;
  const expired = Boolean(active?.validUntil && new Date(active.validUntil) < new Date());

  return (
    <div
      data-testid="ewb-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        !active && required.required ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'bg-card',
      )}
    >
      <span className="inline-flex items-center gap-1.5 font-medium">
        <Truck className="size-4" aria-hidden /> E-way bill
      </span>
      {active?.provider === 'mock' ? (
        <span
          className="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs font-medium text-[#B42318]"
          title={TEST_CONNECTION_HINT}
          data-testid="ewb-test"
        >
          Test e-way bill
        </span>
      ) : null}
      {active ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          <span className="tnum text-foreground" data-testid="ewb-number">
            EWB {active.ewbNo}
          </span>{' '}
          · {formatDateTime(active.ewbDate)}
          {active.generatedVia === 'irn' ? ' · through the IRP' : ''}
          <span className="block">
            {active.validUntil ? (
              <>
                {expired ? 'Expired' : 'Valid until'} {formatDateTime(active.validUntil)}
              </>
            ) : (
              'Part A only: add the vehicle before the goods move'
            )}
            {active.vehicleNo ? ` · Vehicle ${active.vehicleNo}` : ''}
            {active.transDocNo ? ` · Transport doc. ${active.transDocNo}` : ''}
            {active.transporterName ? ` · ${active.transporterName}` : ''}
          </span>
        </span>
      ) : (
        <span className="min-w-48 flex-1 text-muted-foreground" data-testid="ewb-required">
          {bill?.status === 'cancelled' ? `EWB ${bill.ewbNo} was cancelled. ` : ''}
          {required.required ? <span className="text-foreground">Required: </span> : null}
          {required.reason}.
        </span>
      )}
      {staff && !active && goods ? (
        <Button
          size="sm"
          variant={required.required ? 'default' : 'outline'}
          onClick={() => setDialog('generate')}
        >
          Generate e-way bill
        </Button>
      ) : null}
      {staff && active && !expired ? (
        <Button size="sm" variant="outline" onClick={() => setDialog('vehicle')}>
          {active.validUntil ? 'Update vehicle' : 'Add vehicle (Part B)'}
        </Button>
      ) : null}
      {staff && active?.cancellable ? (
        <Button size="sm" variant="ghost" onClick={() => setDialog('cancel')}>
          Cancel e-way bill
        </Button>
      ) : null}
      {dialog === 'generate' ? (
        <GenerateDialog
          documentId={documentId}
          number={number}
          irn={irn}
          subSupply={`${EWB_SUB_SUPPLY[defaults.subSupplyType] ?? defaults.subSupplyType}${
            defaults.subSupplyDesc ? ` (${defaults.subSupplyDesc})` : ''
          }`}
          pins={`${defaults.fromPincode || '—'} → ${defaults.toPincode || '—'}`}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'vehicle' ? (
        <VehicleDialog documentId={documentId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'cancel' && active ? (
        <CancelDialog
          documentId={documentId}
          ewbNo={active.ewbNo}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function GenerateDialog({
  documentId,
  number,
  irn,
  subSupply,
  pins,
  onClose,
}: {
  documentId: string;
  number: string | null;
  irn: string | null;
  subSupply: string;
  pins: string;
  onClose: () => void;
}) {
  const action = useEwayBillAction(documentId);
  const [t, setT] = useState<EwbTransport>({
    distanceKm: 0,
    transMode: 1,
    vehicleNo: '',
    vehicleType: 'R',
    transporterId: '',
    transporterName: '',
    transDocNo: '',
    transDocDate: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const set = <K extends keyof EwbTransport>(k: K, v: EwbTransport[K]) =>
    setT((p) => ({ ...p, [k]: v }));
  const road = t.transMode === 1;
  const submit = () =>
    action.mutate(
      {
        kind: 'generate',
        transport: {
          ...t,
          vehicleNo: t.vehicleNo || null,
          transporterId: t.transporterId || null,
          transporterName: t.transporterName || null,
          transDocNo: t.transDocNo || null,
          transDocDate: t.transDocDate || null,
          // Part A only: the transporter adds the vehicle later
          transMode: t.vehicleNo || t.transDocNo ? t.transMode : null,
        },
      },
      {
        onSuccess: (b) => {
          toast(`E-way bill ${b.ewbNo} generated for ${number ?? 'the document'}.`);
          onClose();
        },
        onError: (e) => setErrors(errorText(e)),
      },
    );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate e-way bill</DialogTitle>
          <DialogDescription>
            {subSupply} · PIN {pins}
            {irn ? ' · generated through the IRP with the IRN' : ''}. Give the vehicle now, or the
            transporter's ID for Part A only.
          </DialogDescription>
        </DialogHeader>
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc pl-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Distance (km) *" hint="0 lets the portal work it out from the PIN codes">
            {(id) => (
              <Input
                id={id}
                className="tnum"
                inputMode="numeric"
                value={String(t.distanceKm)}
                onChange={(e) => set('distanceKm', Number(e.target.value.replace(/\D/g, '') || 0))}
              />
            )}
          </Field>
          <Field label="Mode">
            {(id) => (
              <NativeSelect
                id={id}
                value={String(t.transMode ?? 1)}
                onChange={(e) => set('transMode', Number(e.target.value))}
              >
                {EWB_MODES.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {road ? (
            <>
              <Field label="Vehicle no.">
                {(id) => (
                  <Input
                    id={id}
                    placeholder="MH12AB1234"
                    value={t.vehicleNo ?? ''}
                    onChange={(e) => set('vehicleNo', e.target.value.toUpperCase())}
                  />
                )}
              </Field>
              <Field label="Vehicle type">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={t.vehicleType}
                    onChange={(e) => set('vehicleType', e.target.value as 'R' | 'O')}
                  >
                    <option value="R">Regular</option>
                    <option value="O">Over-dimensional cargo</option>
                  </NativeSelect>
                )}
              </Field>
            </>
          ) : (
            <>
              <Field label="Transport doc. no." hint="RR, airway bill or bill of lading">
                {(id) => (
                  <Input
                    id={id}
                    value={t.transDocNo ?? ''}
                    onChange={(e) => set('transDocNo', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Transport doc. date">
                {(id) => (
                  <DateInput
                    id={id}
                    value={t.transDocDate ?? ''}
                    onChange={(v) => set('transDocDate', v)}
                  />
                )}
              </Field>
            </>
          )}
          <Field label="Transporter ID" hint="GSTIN or TRANSIN">
            {(id) => (
              <Input
                id={id}
                value={t.transporterId ?? ''}
                onChange={(e) => set('transporterId', e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label="Transporter name">
            {(id) => (
              <Input
                id={id}
                value={t.transporterName ?? ''}
                onChange={(e) => set('transporterName', e.target.value)}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={action.isPending} onClick={submit}>
            {action.isPending ? 'Generating…' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VehicleDialog({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const action = useEwayBillAction(documentId);
  const [u, setU] = useState({
    transMode: 1,
    vehicleNo: '',
    transDocNo: '',
    transDocDate: '',
    fromPlace: '',
    fromState: orgLocale().stateCode ?? '',
    reasonCode: 4,
    reasonRemark: '',
    distanceKm: 0,
  });
  const [errors, setErrors] = useState<string[]>([]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update vehicle (Part B)</DialogTitle>
          <DialogDescription>
            A new vehicle or transport document restarts the validity from now for the distance
            left.
          </DialogDescription>
        </DialogHeader>
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc pl-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Reason">
            {(id) => (
              <NativeSelect
                id={id}
                value={String(u.reasonCode)}
                onChange={(e) => setU((p) => ({ ...p, reasonCode: Number(e.target.value) }))}
              >
                {EWB_VEHICLE_REASONS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Mode">
            {(id) => (
              <NativeSelect
                id={id}
                value={String(u.transMode)}
                onChange={(e) => setU((p) => ({ ...p, transMode: Number(e.target.value) }))}
              >
                {EWB_MODES.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {u.transMode === 1 ? (
            <Field label="Vehicle no. *">
              {(id) => (
                <Input
                  id={id}
                  value={u.vehicleNo}
                  onChange={(e) => setU((p) => ({ ...p, vehicleNo: e.target.value.toUpperCase() }))}
                />
              )}
            </Field>
          ) : (
            <Field label="Transport doc. no. *">
              {(id) => (
                <Input
                  id={id}
                  value={u.transDocNo}
                  onChange={(e) => setU((p) => ({ ...p, transDocNo: e.target.value }))}
                />
              )}
            </Field>
          )}
          <Field label="Distance left (km)">
            {(id) => (
              <Input
                id={id}
                className="tnum"
                inputMode="numeric"
                value={String(u.distanceKm)}
                onChange={(e) =>
                  setU((p) => ({
                    ...p,
                    distanceKm: Number(e.target.value.replace(/\D/g, '') || 0),
                  }))
                }
              />
            )}
          </Field>
          <Field label="From place *">
            {(id) => (
              <Input
                id={id}
                value={u.fromPlace}
                onChange={(e) => setU((p) => ({ ...p, fromPlace: e.target.value }))}
              />
            )}
          </Field>
          <Field label="From state *">
            {(id) => (
              <NativeSelect
                id={id}
                value={u.fromState}
                onChange={(e) => setU((p) => ({ ...p, fromState: e.target.value }))}
              >
                <option value="" />
                {GST_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {code} · {name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={action.isPending}
            onClick={() =>
              action.mutate(
                {
                  kind: 'vehicle',
                  update: {
                    ...u,
                    vehicleNo: u.vehicleNo || null,
                    transDocNo: u.transDocNo || null,
                    transDocDate: u.transDocDate || null,
                    reasonRemark: u.reasonRemark || null,
                  },
                },
                {
                  onSuccess: (b) => {
                    toast(`E-way bill ${b.ewbNo} updated.`);
                    onClose();
                  },
                  onError: (e) => setErrors(errorText(e)),
                },
              )
            }
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({
  documentId,
  ewbNo,
  onClose,
}: {
  documentId: string;
  ewbNo: string;
  onClose: () => void;
}) {
  const action = useEwayBillAction(documentId);
  const [reasonCode, setReasonCode] = useState(3);
  const [remark, setRemark] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel e-way bill {ewbNo}</DialogTitle>
          <DialogDescription>
            An e-way bill can be cancelled within 24 hours of generation, before the goods are
            verified in transit. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <Field label="Reason">
          {(id) => (
            <NativeSelect
              id={id}
              value={String(reasonCode)}
              onChange={(e) => setReasonCode(Number(e.target.value))}
            >
              {EWB_CANCEL_REASONS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label="Remark">
          {(id) => (
            <Input
              id={id}
              value={remark}
              maxLength={50}
              onChange={(e) => setRemark(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button
            disabled={remark.trim().length < 3 || action.isPending}
            onClick={() =>
              action.mutate(
                { kind: 'cancel', reasonCode, remark: remark.trim() },
                {
                  onSuccess: () => {
                    toast(`E-way bill ${ewbNo} cancelled.`);
                    onClose();
                  },
                  onError: (e) => toast(e.message, { tone: 'error' }),
                },
              )
            }
          >
            Cancel e-way bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
